import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetScrollView,
} from "@gorhom/bottom-sheet";
import { Pressable, useWindowDimensions, View } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import CloseIcon from "@/assets/svgs/close-icon";
import { Text } from "react-native";
import { Button } from "../ui/button";
import ChatInactiveIcon from "@/assets/svgs/chat-inactive-icon";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import Loader from "../shared/loader";

remapProps(BottomSheetScrollView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type ParentConnectSheetPayload = {
  inTouchWith: string;
  studentName: string;
  studentId: Id<"students">;
};

export type ParentConnectSheetResult = boolean;

function ParentConnectSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    ParentConnectSheetPayload,
    ParentConnectSheetResult
  >(SheetIds.PARENT_CONNECT);
  const { inTouchWith, studentName, studentId } = sheet?.payload ?? {};
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  const { data: studentDetails, isLoading } = useQuery(
    convexQuery(
      api.tutor.inbox.queries.getStudentDetails,
      studentId ? { studentId } : "skip"
    )
  );

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(async () => {
    close();
  }, [close]);

  const onStartChat = useCallback(async () => {
    close(true);
  }, [close]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      enableDynamicSizing
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetScrollView>
        <View className="bg-teachLoop-creamBase rounded-t-[20]">
          <View className="h-6" />
          <View className="mx-5 flex-row">
            <Text className="font-geologica-medium text-black text-xl leading-[1.5] flex-1">
              {studentName}
            </Text>
            <Pressable onPress={onClose} className="active:opacity-80">
              <CloseIcon
                className="size-6"
                pathClassName="stroke-teachLoop-textPrimary"
              />
            </Pressable>
          </View>

          {isLoading && <Loader />}

          {studentDetails && (
            <View className="mx-5 mt-6 gap-4">
              {studentDetails.yearGroup && (
                <View>
                  <Text className="font-geologica-medium text-gray-500 text-sm leading-[1.5]">
                    Year Group
                  </Text>
                  <Text className="font-geologica-regular text-black text-base leading-[1.5]">
                    {studentDetails.yearGroup}
                  </Text>
                </View>
              )}

              {studentDetails.subjects.length > 0 && (
                <View>
                  <Text className="font-geologica-medium text-gray-500 text-sm leading-[1.5]">
                    Subjects
                  </Text>
                  <Text className="font-geologica-regular text-black text-base leading-[1.5]">
                    {studentDetails.subjects.map((s) => s?.label).join(", ")}
                  </Text>
                </View>
              )}

              {studentDetails.learningPreferences.length > 0 && (
                <View>
                  <Text className="font-geologica-medium text-gray-500 text-sm leading-[1.5]">
                    Learning Preferences
                  </Text>
                  <Text className="font-geologica-regular text-black text-base leading-[1.5]">
                    {studentDetails.learningPreferences.join(", ")}
                  </Text>
                </View>
              )}

              {studentDetails.additionalLearningNeeds && (
                <View>
                  <Text className="font-geologica-medium text-gray-500 text-sm leading-[1.5]">
                    Additional Learning Needs
                  </Text>
                  <Text className="font-geologica-regular text-black text-base leading-[1.5]">
                    {studentDetails.additionalLearningNeeds}
                  </Text>
                </View>
              )}
            </View>
          )}

          <View className="h-6" />
          <View className="mx-5">
            <Text className="font-geologica-regular text-black text-base leading-[1.5]">
              Get in touch with {inTouchWith}
            </Text>
          </View>
          <View className="h-6" />
          <View className="items-end mx-5">
            <Button intent="primary" size="sm" onPress={onStartChat} icon>
              <Button.Icon
                as={ChatInactiveIcon}
                pathClassName="stroke-white stroke-[2]"
              />
              <Button.Text size="sm" className="text-base">
                Start a chat
              </Button.Text>
            </Button>
          </View>
          <View className="h-8" />
        </View>
      </BottomSheetScrollView>
    </BottomSheet>
  ) : null;
}

export default ParentConnectSheet;
